import axios from 'axios';
import { getApiBaseUrl } from './api';

export interface BillingCard {
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
}

export interface BillingAccount {
  billed: boolean;
  not_billed_source?: 'super_admin' | null;
  can_manage: boolean;
  billing_email: string | null;
  card: BillingCard | null;
  plan_payer_email?: string | null;
  viewer_email?: string;
}

const base = () => `${getApiBaseUrl()}/billing/account`;

export interface SeatSummary {
  billed: boolean;
  can_manage?: boolean;
  plan?: 'free' | 'workspace' | 'legacy';
  plan_name?: string;
  seats?: number;
  joined?: number;
  pending_invites?: number;
  max_seats?: number;
  allowance_per_seat_cents?: number;
  seat_price_cents?: number;
  monthly_cents?: number;
  renews_at?: string | null;
  cancel_at_period_end?: boolean;
  ended_plan?: { seats: number | null; ended_at: string | null; stopped_by_us: boolean } | null;
}

export interface SeatHolder {
  email: string;
  name: string | null;
  role: string | null;
  joined_at: string | null;
}

export interface RenewalSeat {
  number: number;
  added_at: string | null;
  holder: SeatHolder | null;
  renews_at: string | null;
}

export interface RenewalLine {
  description: string;
  amount_cents: number;
  quantity: number;
  kind: 'seats' | 'hosting' | 'other';
}

export interface SeatRenewal {
  status: string;
  auto_renew: boolean;
  /* none: every seat renews on its own. ending: renewal is off. unpaid: a renewal payment is waiting. */
  needs: 'none' | 'ending' | 'unpaid';
  renews_at: string | null;
  seat_price_cents: number;
  seat_count: number;
  joined: number;
  seats: RenewalSeat[];
  next_invoice: { at: string | null; total_cents: number; amount_due_cents: number; lines: RenewalLine[] } | null;
  unpaid: { id: string; number: string | null; amount_due_cents: number; created: number; url: string | null }[];
}

export interface WalletEntry {
  kind: 'starter' | 'allowance' | 'topup' | 'period_close' | 'adjustment';
  amount_cents: number;
  description: string;
  at: string;
}

export interface WalletSummary {
  billed: boolean;
  topup_payer_email?: string | null;
  can_manage?: boolean;
  plan?: 'free' | 'workspace' | 'legacy';
  capped?: boolean;
  enforced?: boolean;
  usage_available?: boolean;
  period_start?: string;
  used_cents?: number;
  allowance_cents?: number;
  allowance_used_cents?: number;
  balance_cents?: number;
  remaining_cents?: number;
  paused?: boolean;
  auto_topup?: boolean;
  topup_amount_cents?: number;
  topup_threshold_cents?: number;
  /* Set on a save that checked the balance at once: 'topped_up', 'not_low', or why a top-up did not go through. */
  topup?: string;
  ai_cents?: number;
  compute_cents?: number;
  entries?: WalletEntry[];
}

export type InvoiceKind = 'plan' | 'seats' | 'credit' | 'topup' | 'other';

export interface StripeInvoiceRow {
  id: string;
  number: string | null;
  created: number;
  total_cents: number;
  status: string;
  url: string | null;
  pdf: string | null;
  kind: InvoiceKind;
  title: string;
  detail: string;
}

export interface InvoicePage {
  invoices: StripeInvoiceRow[];
  has_more: boolean;
  next_cursor: string | null;
}

export type UsagePeriod = 'period' | '30d' | 'all';

export interface PersonRef {
  key: string;
  email: string | null;
  name: string | null;
  role: string | null;
  department: string | null;
  kind: 'member' | 'invited' | 'former' | 'unlinked';
}

export interface PersonUsageRow extends PersonRef {
  ai_usd: number;
  compute_usd: number;
  total_usd: number;
  calls: number;
  last_at: string | null;
  trend: number[];
  share: number;
}

export interface PeopleUsage {
  period: UsagePeriod;
  since: string | null;
  billed: boolean;
  available: boolean;
  total_usd?: number;
  ai_usd?: number;
  compute_usd?: number;
  people: PersonUsageRow[];
}

export interface PersonUsageDetail {
  period: UsagePeriod;
  since: string | null;
  person: PersonRef;
  available: boolean;
  company_total_usd?: number | null;
  totals?: {
    aiUsd: number;
    computeUsd: number;
    totalUsd: number;
    calls: number;
    firstAt: string | null;
    lastAt: string | null;
  };
  daily?: { day: string; aiUsd: number; computeUsd: number }[];
  work?: PersonWork;
}

export type WorkRelation = 'internal' | 'retainer' | 'regular' | 'other';
export type WorkKind = 'build' | 'planning' | 'compute';
export type WorkPayer = 'you' | 'your_budget' | 'client_budget' | 'client' | 'other';

export interface PersonWork {
  totals: {
    internal_usd: number;
    retainer_usd: number;
    regular_usd: number;
    other_usd: number;
    total_usd: number;
    charged_to_you_usd: number;
    your_budgets_usd: number;
  };
  companies: {
    id: string | null;
    name: string | null;
    relation: WorkRelation;
    total_usd: number;
    projects: {
      id: string | null;
      title: string | null;
      total_usd: number;
      parts: { kind: WorkKind; paid_by: WorkPayer; usd: number; calls: number }[];
    }[];
  }[];
}

export const billingApi = {
  getWallet: () => axios.get<WalletSummary>(`${getApiBaseUrl()}/billing/wallet`),
  startTopup: (amountCents: number, successUrl: string, cancelUrl: string) =>
    axios.post<{ url: string; session_id: string }>(`${getApiBaseUrl()}/billing/wallet/checkout`, {
      amount_cents: amountCents,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }),
  confirmTopup: (sessionId: string) =>
    axios.post<WalletSummary & { paid_cents: number }>(`${getApiBaseUrl()}/billing/wallet/checkout/confirm`, { session_id: sessionId }),
  setAutoTopup: (enabled: boolean) => axios.patch<WalletSummary>(`${getApiBaseUrl()}/billing/wallet`, { auto_topup: enabled }),
  setTopupRule: (thresholdCents: number, amountCents: number) =>
    axios.patch<WalletSummary>(`${getApiBaseUrl()}/billing/wallet`, {
      topup_threshold_cents: thresholdCents,
      topup_amount_cents: amountCents,
    }),
  listInvoices: (limit = 24, startingAfter?: string | null) =>
    axios.get<InvoicePage>(`${getApiBaseUrl()}/billing/account/invoices`, {
      params: { limit, starting_after: startingAfter || undefined },
    }),
  getPeopleUsage: (period: UsagePeriod) =>
    axios.get<PeopleUsage>(`${getApiBaseUrl()}/billing/usage/people`, { params: { period } }),
  getPersonUsage: (key: string, period: UsagePeriod) =>
    axios.get<PersonUsageDetail>(`${getApiBaseUrl()}/billing/usage/person`, { params: { email: key, period } }),
  getSeats: () => axios.get<SeatSummary>(`${getApiBaseUrl()}/billing/seats`),
  startSeatPlan: (seats: number, successUrl: string, cancelUrl: string) =>
    axios.post<{ url: string; session_id: string }>(`${getApiBaseUrl()}/billing/seats/start`, { seats, success_url: successUrl, cancel_url: cancelUrl }),
  confirmSeatPlan: (sessionId: string) =>
    axios.post<{ activated: boolean }>(`${getApiBaseUrl()}/billing/seats/confirm`, { session_id: sessionId }),
  changeSeats: (seats: number) => axios.put<SeatSummary>(`${getApiBaseUrl()}/billing/seats`, { seats }),
  getRenewal: () => axios.get<SeatRenewal>(`${getApiBaseUrl()}/billing/seats/renewal`),
  setAutoRenew: (autoRenew: boolean) =>
    axios.patch<SeatRenewal>(`${getApiBaseUrl()}/billing/seats/renewal`, { auto_renew: autoRenew }),
  renewAll: () => axios.post<SeatRenewal>(`${getApiBaseUrl()}/billing/seats/renew`),
  getAccount: () => axios.get<BillingAccount>(base()),
  startCardSession: (successUrl: string, cancelUrl: string) =>
    axios.post<{ url: string; session_id: string }>(`${base()}/card-session`, { success_url: successUrl, cancel_url: cancelUrl }),
  confirmCardSession: (sessionId: string) =>
    axios.post<{ card: BillingCard | null }>(`${base()}/card-session/confirm`, { session_id: sessionId }),
  updateBillingEmail: (billingEmail: string) =>
    axios.patch<{ billing_email: string; card: BillingCard | null }>(base(), { billing_email: billingEmail }),
//   transferOwnership: (companyId: string, email: string) =>
//     axios.post<{ owner_email: string; previous_owner_role: string }>(
//       `${getApiBaseUrl()}/workspace/${companyId}/transfer-ownership`,
//       { email },
//     ),
};
